import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  COOKIE_PREFERENCES_OPEN_EVENT,
  readConsent,
  writeConsent,
  CookieConsentAction,
  CookieConsentPrefs,
} from '@/utils/cookieConsent';

function resolvePolicyPath(pathname: string): string {
  if (pathname.startsWith('/maringa')) return '/maringa/politica-de-privacidade';
  if (pathname.startsWith('/goias')) return '/goias/politica-de-privacidade';
  return '/goiania/politica-de-privacidade';
}

export default function CookieConsentBanner() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  // Primeira visita: mostra o banner com uma leve animação
  useEffect(() => {
    const saved = readConsent();
    if (saved) {
      setAnalytics(saved.analytics);
      setMarketing(saved.marketing);
      return undefined;
    }

    setVisible(true);
    const timer = window.setTimeout(() => setMounted(true), 400);
    return () => window.clearTimeout(timer);
  }, []);

  // Reabertura pelas "Preferências de cookies" no rodapé
  useEffect(() => {
    const handleOpen = () => {
      const saved = readConsent();
      setAnalytics(saved ? saved.analytics : true);
      setMarketing(saved ? saved.marketing : false);
      setShowPrefs(true);
      setVisible(true);
      window.setTimeout(() => setMounted(true), 50);
    };

    window.addEventListener(COOKIE_PREFERENCES_OPEN_EVENT, handleOpen);
    return () => window.removeEventListener(COOKIE_PREFERENCES_OPEN_EVENT, handleOpen);
  }, []);

  const persist = (prefs: CookieConsentPrefs, action: CookieConsentAction) => {
    writeConsent(prefs, action);
    setMounted(false);
    window.setTimeout(() => setVisible(false), 260);
  };

  const acceptAll = () => persist({ analytics: true, marketing: true }, 'accept_all');
  const rejectOptional = () => persist({ analytics: false, marketing: false }, 'reject_optional');
  const savePrefs = () => persist({ analytics, marketing }, 'custom');

  if (!visible) {
    return null;
  }

  const policyPath = resolvePolicyPath(location.pathname);

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] p-3 md:p-5 pointer-events-none">
      <div
        role="dialog"
        aria-label="Consentimento de cookies"
        className={`pointer-events-auto mx-auto w-full max-w-3xl rounded-2xl border border-background-200 bg-background-50 transition-all duration-300 ${
          mounted ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
        }`}
      >
        {!showPrefs ? (
          <div className="p-5 md:p-6 flex flex-col lg:flex-row lg:items-center gap-5">
            <div className="flex items-start gap-4 flex-1 min-w-0">
              <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center shrink-0">
                <i className="ri-shield-keyhole-line text-xl text-primary-600" />
              </div>
              <div className="min-w-0">
                <p className="text-sm md:text-base font-heading font-semibold text-foreground-950 mb-1">
                  Sua privacidade importa
                </p>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                  Usamos cookies para garantir o funcionamento do portal e, com o seu consentimento,
                  para análise de uso e melhoria da experiência.{' '}
                  <Link to={policyPath} className="text-primary-600 font-medium hover:underline">
                    Política de Privacidade
                  </Link>
                  .
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 lg:shrink-0">
              <button
                type="button"
                onClick={() => setShowPrefs(true)}
                className="px-4 py-2.5 rounded-lg text-sm font-semibold text-secondary-900 bg-secondary-100 hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                Configurar
              </button>
              <button
                type="button"
                onClick={rejectOptional}
                className="px-4 py-2.5 rounded-lg text-sm font-semibold text-foreground-700 bg-background-100 border border-background-200 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                Recusar
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="px-5 py-2.5 rounded-lg text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Aceitar todos
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 md:p-6">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center shrink-0">
                <i className="ri-equalizer-line text-xl text-primary-600" />
              </div>
              <div>
                <p className="text-sm md:text-base font-heading font-semibold text-foreground-950 mb-1">
                  Preferências de cookies
                </p>
                <p className="text-xs md:text-sm text-foreground-600">
                  Escolha quais categorias deseja permitir. Você pode alterar isso a qualquer momento.
                </p>
              </div>
            </div>

            <div className="flex flex-col divide-y divide-background-200/70 border-y border-background-200/70">
              <div className="flex items-center justify-between gap-4 py-3.5">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground-950">Estritamente necessários</p>
                  <p className="text-xs text-foreground-500 mt-0.5">
                    Essenciais para o funcionamento e a segurança do portal.
                  </p>
                </div>
                <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground-400 shrink-0">
                  Sempre ativos
                </span>
              </div>

              <div className="flex items-center justify-between gap-4 py-3.5">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground-950">Análise e desempenho</p>
                  <p className="text-xs text-foreground-500 mt-0.5">
                    Ajudam a entender como o portal é usado, de forma agregada.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={analytics}
                  aria-label="Permitir cookies de análise"
                  onClick={() => setAnalytics((value) => !value)}
                  className={`relative w-11 h-6 rounded-full transition-colors shrink-0 cursor-pointer ${
                    analytics ? 'bg-primary-500' : 'bg-background-300'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-background-50 transition-transform ${
                      analytics ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between gap-4 py-3.5">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground-950">Marketing e personalização</p>
                  <p className="text-xs text-foreground-500 mt-0.5">
                    Usados para personalizar conteúdos e divulgar campanhas.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={marketing}
                  aria-label="Permitir cookies de marketing"
                  onClick={() => setMarketing((value) => !value)}
                  className={`relative w-11 h-6 rounded-full transition-colors shrink-0 cursor-pointer ${
                    marketing ? 'bg-primary-500' : 'bg-background-300'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-background-50 transition-transform ${
                      marketing ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2.5 mt-5">
              <button
                type="button"
                onClick={rejectOptional}
                className="px-4 py-2.5 rounded-lg text-sm font-semibold text-foreground-700 bg-background-100 border border-background-200 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                Recusar opcionais
              </button>
              <button
                type="button"
                onClick={savePrefs}
                className="px-5 py-2.5 rounded-lg text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Salvar preferências
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}